import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SyntheticEvent } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { css } from '@emotion/react';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ID } from '@e-socna/empty-client';
import { getDocumentName } from '@e-socna/data-layer';
import type { Id, ReportStateId } from '@e-socna/data-layer';

import { useIllustrationUrl } from '../../components/Illustrations/hooks/useIllustrationUrl.hook';
import { AttachedFileIllustration } from '../../components/Illustrations/AttachedFile/AttachedFile';

import { PATH_MISSION_REPORT_DEFAULT } from '../../app/constants/paths.constant';
import { emptyMemosState } from '../../forms/MemoBeforeSubmit';
import { usePrinterContext } from '../../contexts/PrinterContext/hooks/usePrinterContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useColorToMuiColor } from '../../hooks/useColorToMuiColor.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useFindAllDocumentTemplatesQuery } from '../DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import { MissionReportInfoStateForm } from '../../forms/MissionReportInfoState';
import { Avatar } from '../../components/Avatar';
import { EllipsisTooltip } from '../../components/EllipsisTooltip';
import { MenuItem } from '../../components/Menu/MenuItem';
import { RightDrawer } from '../../components/RightDrawer';
import { PdfViewer } from '../../components/PdfViewer';
import { DisabledFeatureInStrictMode } from '../../components/DisabledFeatureInStrictMode';
import { UserName } from '../../components/UserName';
import { iconToMuiIcon } from '../../utils/icon-to-mui-icon.util';
import { formatDateTime } from '../../utils/format-date-time.util';
import { environment } from '../../environment';

import type { MissionReportPage_ReportInfo } from '../MissionReport/fragments/report-info.fragment';

import { DEFAULT_REPORT_PLACEHOLDER_HEIGHT } from './constants/card.constant';
import { MissionReportsPage_FindSomeReports_QueryDocument } from './hooks/useFindAllMissionReportsQuery.hook';
import type { MissionReportsPage_SomeReports } from './hooks/useFindAllMissionReportsQuery.hook';

interface Props {
  readonly item: Readonly<{
    _id: Id;
    missionReportInfo: MissionReportPage_ReportInfo;
    report: MissionReportsPage_SomeReports[number] | undefined;
  }>;
}

type PdfPrint =
  MissionReportsPage_SomeReports[number]['printing']['pdfPrints'][number];

const getLatestPdfPrint = (
  pdfPrints: readonly PdfPrint[] | undefined,
  isTmp: boolean,
): PdfPrint | undefined =>
  pdfPrints
    ?.filter((pdfPrint) => pdfPrint.isTmp === isTmp)
    .reduce<PdfPrint | undefined>(
      (latest, pdfPrint) =>
        !latest || pdfPrint.increment > latest.increment ? pdfPrint : latest,
      undefined,
    );

export const ReportCard = ({
  item: { missionReportInfo, report },
}: Readonly<Props>) => {
  const imageUrl = useIllustrationUrl(AttachedFileIllustration);
  const { folderId, missionId } = useMissionReadyContext();

  const { paths } = useBuildPaths(
    { report: PATH_MISSION_REPORT_DEFAULT },
    { folderId, missionId, reportInfoId: missionReportInfo._id },
  );

  const { colorToMuiColor } = useColorToMuiColor();
  const { reportStatesIdMap } = useReferencesContext();

  const { reportState } = ensureDefined({
    reportState: reportStatesIdMap[missionReportInfo.stateId as ReportStateId],
  });

  const { data: templatesData } = useFindAllDocumentTemplatesQuery();

  const reportTemplateLabel = templatesData?.findAllDocumentTemplates.find(
    (template) => template._id === report?.templateId,
  )?.label;

  const [isInEditionMode, setIsInEditionMode] = useState(false);

  const enterEditionMode = useCallback(() => {
    setIsInEditionMode(true);
  }, []);

  const leaveEditionMode = useCallback(() => {
    setIsInEditionMode(false);
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isActionMenuOpen = Boolean(anchorEl);

  const handleOpenActionMenu = useCallback(
    (event: Readonly<SyntheticEvent<HTMLButtonElement>>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseActionMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const latestPdfPrint = useMemo(
    () =>
      getLatestPdfPrint(report?.printing.pdfPrints, false) ??
      getLatestPdfPrint(report?.printing.pdfPrints, true),
    [report?.printing.pdfPrints],
  );

  const pdfUrl = latestPdfPrint
    ? `${environment.backendUrl}/${latestPdfPrint.file.uri}`
    : undefined;

  const isPdfUpToDate =
    !!latestPdfPrint &&
    !!report &&
    new Date(latestPdfPrint.createdAt) >= new Date(report.contentUpdatedAt);

  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);

  const openPdfDrawer = useCallback(() => {
    setIsPdfDrawerOpen(true);
  }, []);

  const closePdfDrawer = useCallback(() => {
    setIsPdfDrawerOpen(false);
  }, []);

  const { print } = usePrinterContext();
  const [isRefreshingPdf, setIsRefreshingPdf] = useState(false);

  const queryKeyToInvalidate = useMemo(
    () => [
      getDocumentName(MissionReportsPage_FindSomeReports_QueryDocument),
      { filter: { missionId } },
    ],
    [missionId],
  );

  const handleRefreshPdf = useCallback(() => {
    if (!report) {
      return;
    }

    setIsRefreshingPdf(true);

    print({
      clearTmp: true,
      command: 'printMissionReport',
      handleError: () => {
        setIsRefreshingPdf(false);
      },
      handleSuccess: () => {
        setIsRefreshingPdf(false);
      },
      isTmp: false,
      memos: emptyMemosState,
      printableId: report._id,
      queryKeysToInvalidate: [queryKeyToInvalidate],
      snackbarActionTarget: paths.report,
      useCacheFirst: false,
    });
  }, [paths.report, print, queryKeyToInvalidate, report]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardHeader
        action={
          <>
            <Tooltip title="Actions sur le Rapport">
              <IconButton
                aria-controls={
                  isActionMenuOpen
                    ? `${missionReportInfo._id}-actions-menu`
                    : undefined
                }
                aria-expanded={isActionMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
                aria-label="Actions sur le rapport"
                id={`${missionReportInfo._id}-actions-menu-button`}
                onClick={handleOpenActionMenu}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              id={`${missionReportInfo._id}-actions-menu`}
              onClose={handleCloseActionMenu}
              open={isActionMenuOpen}
              slotProps={{
                list: {
                  'aria-labelledby': `${missionReportInfo._id}-actions-menu-button`,
                },
              }}
            >
              <MenuItem
                onCloseMenu={handleCloseActionMenu}
                option={{
                  action: enterEditionMode,
                  children: (
                    <>
                      <ListItemIcon>
                        <SyncIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Modifier État</ListItemText>
                    </>
                  ),
                  key: 'state',
                  title: 'Modifier État',
                }}
              />
            </Menu>
          </>
        }
        avatar={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Avatar
              bgColor={colorToMuiColor(reportState.chip.color)}
              icon={iconToMuiIcon(reportState.chip.icon)}
              label={`État : ${reportState.label}`}
            />
            {!!report && (
              <Avatar
                bgColor="action.disabledBackground"
                icon={
                  report.isEditable ? (
                    <LockOpenIcon fontSize="small" />
                  ) : (
                    <LockIcon fontSize="small" />
                  )
                }
                label={
                  report.isEditable
                    ? 'Document modifiable'
                    : 'Document verrouillé'
                }
              />
            )}
          </Stack>
        }
        css={css`
          .MuiCardHeader-action {
            display: contents;
          }
        `}
      />
      <CardMedia
        alt="Aperçu du rapport"
        component="img"
        height={DEFAULT_REPORT_PLACEHOLDER_HEIGHT}
        image={imageUrl}
        sx={{ objectFit: 'contain' }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={1}>
          <Typography component="h2" variant="h6">
            {report
              ? report.title
              : missionReportInfo.reportId
                ? 'Chargement ...'
                : 'Document en cours de création ...'}
          </Typography>
          {!!report && (
            <>
              <EllipsisTooltip title={report.code}>
                <Typography color="text.secondary" noWrap variant="body2">
                  {report.code}
                </Typography>
              </EllipsisTooltip>
              <Typography color="text.secondary" variant="body2">
                Modèle : {reportTemplateLabel ?? 'Aucun'}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {formatDateTime(report.updatedAt, {
                  datePrefix: 'Modifié le ',
                })}{' '}
                par <UserName id={report.updaterId} />
              </Typography>
            </>
          )}
        </Stack>
      </CardContent>
      <CardActions>
        <Button component={Link} to={paths.report} variant="outlined">
          Consulter
        </Button>
        <Tooltip title={pdfUrl ? 'Consulter le PDF' : 'Aucun PDF disponible'}>
          <span style={{ marginLeft: 'auto' }}>
            <Fab
              aria-label="Consulter le PDF"
              disabled={!pdfUrl}
              onClick={openPdfDrawer}
              size="small"
            >
              <PictureAsPdfIcon />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip
          title={
            isPdfUpToDate
              ? 'PDF à jour'
              : 'Rafraîchir le PDF (génère une nouvelle version)'
          }
        >
          <span>
            <Fab
              aria-label={isPdfUpToDate ? 'PDF à jour' : 'Rafraîchir le PDF'}
              color={isPdfUpToDate ? 'success' : 'primary'}
              disabled={isPdfUpToDate || !report || isRefreshingPdf}
              onClick={handleRefreshPdf}
              size="small"
              sx={
                isPdfUpToDate
                  ? {
                      '&.Mui-disabled': {
                        backgroundColor: 'success.main',
                        color: 'success.contrastText',
                      },
                    }
                  : undefined
              }
            >
              {isPdfUpToDate ? <CheckCircleIcon /> : <RefreshIcon />}
            </Fab>
          </span>
        </Tooltip>
      </CardActions>
      <RightDrawer
        isOpen={isPdfDrawerOpen}
        onDrawerClose={closePdfDrawer}
        title="Visionneuse"
      >
        {!!pdfUrl && (
          <DisabledFeatureInStrictMode>
            <PdfViewer showControls url={pdfUrl} />
          </DisabledFeatureInStrictMode>
        )}
      </RightDrawer>
      <MissionReportInfoStateForm
        enterEditionMode={enterEditionMode}
        isInEditionMode={isInEditionMode}
        leaveEditionMode={leaveEditionMode}
        missionId={missionId}
        missionReportInfo={missionReportInfo}
        reportId={missionReportInfo.reportId ?? EMPTY_ID}
        showSummary={false}
      />
    </Card>
  );
};
